import {
  ChartGroupLayout,
  DashboardChartDto,
} from "@features/results/Result/Dashboard/dashboardModel"
import { FunctionComponent, useState } from "react"
import { DrawerChartList } from "@features/results/Result/Dashboard/DrawerChartList"
import { IconButton, Typography } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"

interface Props {
  charts: DashboardChartDto[]
  layouts?: ChartGroupLayout[]
  runKeys: string[]
  selectedRunKey: string
}

interface DashboardChartSection {
  charts: DashboardChartDto[]
  layout?: ChartGroupLayout
  title?: string
}

const convertToSections = (
  charts: DashboardChartDto[],
  layouts: ChartGroupLayout[],
): DashboardChartSection[] => {
  const output: DashboardChartSection[] = []
  // order of sections is order of layouts in array that's why we pick layouts first
  layouts.forEach((layout) => {
    const chart = charts.filter((ch) => ch.Section === layout.Section)
    if (chart.length === 0) return
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    output.push({ title: chart[0].Section, charts: chart, layout })
  })

  // not every section can have layout, so we add sections without layouts on top of sections with layout
  const chartsWithoutLayout: string[] = []
  charts.forEach((chart) => {
    if (!chart.Section) return
    const layout = layouts.filter((l) => l.Section === chart.Section)
    if (layout.length !== 0) return
    if (!chartsWithoutLayout.includes(chart.Section)) chartsWithoutLayout.push(chart.Section)
  })
  chartsWithoutLayout.reverse().forEach((sectionName) =>
    output.unshift({
      charts: charts.filter((ch) => ch.Section === sectionName),
      title: sectionName,
    }),
  )

  // charts without section and layout go on top of everything
  output.unshift({ charts: charts.filter((ch) => !ch.Section) })

  return output
}

export const DrawerSectionList: FunctionComponent<Props> = ({
  charts,
  runKeys,
  selectedRunKey,
  layouts = [],
}) => {
  const sectionsData = convertToSections(charts, layouts)
  return (
    <div>
      {sectionsData.map((s, i) => (
        <CollapsableSection
          key={`dashboard-section-${i}`}
          runKeys={runKeys}
          selectedRunKey={selectedRunKey}
          section={s}
        />
      ))}
    </div>
  )
}

interface CollapsableSectionProps {
  runKeys: string[]
  selectedRunKey: string
  section: DashboardChartSection
}

const CollapsableSection: FunctionComponent<CollapsableSectionProps> = ({
  runKeys,
  selectedRunKey,
  section,
}) => {
  const [open, setOpen] = useState(true)

  if (section.charts.length === 0) return <></>

  return (
    <div>
      <div
        style={{
          display: "flex",
          borderTop: "1px solid #ccc",
          outlineOffset: "0px",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Akkurat-Bold",
            lineHeight: "36px",
            fontSize: "36px",
            letterSpacing: "0.18px",
            color: "#172B4D",
            margin: "44px 0px",
            marginBottom: 0,
          }}
        >
          {section.title || ""}
        </Typography>
        <IconButton color="primary" onClick={() => setOpen(!open)}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>

      <div style={{ width: "100%", marginBottom: 20, display: open ? "inline-block" : "none" }}>
        <DrawerChartList
          runKeys={runKeys}
          selectedRunKey={selectedRunKey}
          charts={section.charts}
          layout={section.layout}
        />
      </div>
    </div>
  )
}
